import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Landmark } from 'lucide-react';
import api, { errMsg } from '../lib/api';
import { CATEGORIES, STATUS_LABELS } from '../lib/shop';
import { Field, Tgl, inp } from './components';

export default function AdminBankTransfer() {
  const [bt, setBt] = useState(null);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/settings/payments').then((r) => {
      setBt({
        enabled: false, account_name: '', sort_code: '', account_number: '', iban: '', swift: '',
        bank_name: '', instructions: '', due_period_days: 3, customer_note: '',
        restrictions: { categories: [], product_ids: [], statuses: [] },
        ...(r.data.bank_transfer || {}),
      });
    }).catch((e) => toast.error(errMsg(e)));
    api.get('/admin/products').then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setBt((p) => ({ ...p, [k]: v }));
  const restr = (k, v) => setBt((p) => ({ ...p, restrictions: { ...(p.restrictions || {}), [k]: v } }));
  const toggleIn = (list, val) => (list || []).includes(val) ? list.filter((x) => x !== val) : [...(list || []), val];

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings/payments', { bank_transfer: bt });
      toast.success('Manual Bank Transfer settings saved');
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!bt) return null;
  const r = bt.restrictions || {};
  const chip = (active) => `px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-wider border transition-colors ${active ? 'bg-[#0B0F12] text-white border-[#0B0F12]' : 'border-[#D8D8D0] hover:border-[#0B0F12]'}`;

  return (
    <div className="border hairline bg-white p-6 space-y-4 max-w-3xl mt-8" data-testid="bank-transfer-settings">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-display font-bold uppercase text-xl flex items-center gap-2"><Landmark className="w-5 h-5" /> Manual Bank Transfer</p>
          <p className="text-[12px] text-[#575B60] mt-1">Customer pays by direct transfer using their order reference. Orders stay Awaiting Payment until you confirm receipt. Only the receiving account details below are stored — never online-banking credentials.</p>
        </div>
        <span className={`font-mono2 text-[10px] uppercase tracking-widest border px-2.5 py-1 ${bt.enabled ? 'border-[#6EE7B7] bg-[#ECFDF5] text-[#065F46]' : 'hairline text-[#8A8F95]'}`} data-testid="bt-status">
          {bt.enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>

      <Tgl label="Enable Manual Bank Transfer at checkout" checked={!!bt.enabled} onChange={(v) => set('enabled', v)} testid="bt-enabled" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Account name"><input data-testid="bt-account-name" value={bt.account_name || ''} onChange={(e) => set('account_name', e.target.value)} className={inp} /></Field>
        <Field label="Bank name"><input data-testid="bt-bank-name" value={bt.bank_name || ''} onChange={(e) => set('bank_name', e.target.value)} className={inp} /></Field>
        <Field label="Sort code"><input data-testid="bt-sort-code" value={bt.sort_code || ''} onChange={(e) => set('sort_code', e.target.value)} className={`${inp} font-mono2`} placeholder="00-00-00" /></Field>
        <Field label="Account number"><input data-testid="bt-account-number" value={bt.account_number || ''} onChange={(e) => set('account_number', e.target.value)} className={`${inp} font-mono2`} placeholder="00000000" /></Field>
        <Field label="IBAN (optional)"><input data-testid="bt-iban" value={bt.iban || ''} onChange={(e) => set('iban', e.target.value)} className={`${inp} font-mono2`} /></Field>
        <Field label="SWIFT/BIC (optional)"><input data-testid="bt-swift" value={bt.swift || ''} onChange={(e) => set('swift', e.target.value)} className={`${inp} font-mono2`} /></Field>
        <Field label="Payment due period (days, 0 = no expiry shown)"><input data-testid="bt-due-days" type="number" min="0" value={bt.due_period_days ?? 3} onChange={(e) => set('due_period_days', e.target.value)} className={inp} /></Field>
      </div>
      <Field label="Payment instructions (shown on order page + email)"><textarea data-testid="bt-instructions" rows={3} value={bt.instructions || ''} onChange={(e) => set('instructions', e.target.value)} className={inp} /></Field>
      <Field label="Customer-facing note (shown at checkout)"><input data-testid="bt-customer-note" value={bt.customer_note || ''} onChange={(e) => set('customer_note', e.target.value)} className={inp} /></Field>

      <div className="border-t hairline pt-4 space-y-4">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">Eligibility restrictions (empty = all products eligible)</p>
        <Field label="Only these categories">
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(CATEGORIES).map(([k, c]) => (
              <button key={k} type="button" data-testid={`bt-restrict-cat-${k}`} onClick={() => restr('categories', toggleIn(r.categories, k))} className={chip((r.categories || []).includes(k))}>{c.name}</button>
            ))}
          </div>
        </Field>
        <Field label="Only products with these statuses">
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(STATUS_LABELS).filter(([k]) => k !== 'hidden').map(([k, l]) => (
              <button key={k} type="button" data-testid={`bt-restrict-status-${k}`} onClick={() => restr('statuses', toggleIn(r.statuses, k))} className={chip((r.statuses || []).includes(k))}>{l}</button>
            ))}
          </div>
        </Field>
        <Field label="Only these products">
          <div className="border hairline max-h-44 overflow-y-auto divide-y divide-[#F0F0EA]">
            {products.map((p) => (
              <label key={p.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-[#FAFAF8]">
                <input type="checkbox" data-testid={`bt-restrict-product-${p.id}`} checked={(r.product_ids || []).includes(p.id)}
                  onChange={() => restr('product_ids', toggleIn(r.product_ids, p.id))} className="w-4 h-4 accent-[#0B0F12]" />
                <span className="text-sm">{p.name} {p.strength && <span className="text-[#8A8F95]">{p.strength}</span>}</span>
              </label>
            ))}
          </div>
        </Field>
        <p className="text-[11px] text-[#8A8F95]">Restrictions combine: a basket is eligible only if every item passes all configured lists.</p>
      </div>

      <button data-testid="bt-save" onClick={save} disabled={saving}
        className="bg-[#0B0F12] text-white px-6 py-3 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#1F8A58] transition-colors disabled:opacity-50">
        {saving ? 'Saving…' : 'Save bank transfer settings'}
      </button>
    </div>
  );
}
